import React, { useRef } from 'react';
import { bool, func, number, string } from 'prop-types';
import { Form as FinalForm, FormSpy } from 'react-final-form';

import config from '../../../config';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { numberAtLeast, required } from '../../../util/validators';

import {
  Form,
  FieldSelect,
  FieldTextInput,
  InlineTextButton,
  PrimaryButton,
} from '../../../components';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';

import css from './ProductOrderForm.module.css';
import { useSelector } from 'react-redux';

const renderForm = formRenderProps => {
  const {
    // FormRenderProps from final-form
    handleSubmit,
    form: formApi,

    // Custom props passed to the form component
    intl,
    formId,
    currentStock,
    hasMultipleDeliveryMethods,
    listingId,
    isOwnListing,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    values,
    transaction,
    isStripeConnected,
    currentUser,
    isUnavailable,
    isClosed,
  } = formRenderProps;

  const state = useSelector(state => state);
  const { authorData } = state.ListingPage;
  const { sellerShippingAddress = [] } =
    (!!authorData?.id && authorData.attributes.profile.protectedData) || {};
  const showEmailVerification = !!currentUser?.id && currentUser.attributes.emailVerified;
  const { offerPrice, counterPrice } =
    (transaction?.id && transaction?.attributes?.protectedData) || {};
  const { shippingOrder = {} } = transaction?.attributes?.metadata || {};
  const handleOnChange = formValues => {
    const { quantity: quantityRaw, deliveryMethod } = formValues.values;
    const quantity = Number.parseInt(quantityRaw, 10);
    const isBrowser = typeof window !== 'undefined';
    if (isBrowser && quantity && deliveryMethod && !fetchLineItemsInProgress) {
      onFetchTransactionLineItems({
        orderData: {
          quantity,
          deliveryMethod,
          offerPrice,
          counterPrice,
          shippingPrice: shippingOrder?.data?.shipping_price || shippingOrder?.shipping_price,
        },
        listingId,
        isOwnListing,
      });
    }
  };

  // In case quantity and deliveryMethod are missing focus on that select-input.
  // Otherwise continue with the default handleSubmit function.
  const handleFormSubmit = e => {
    const { quantity, deliveryMethod } = values || {};
    // if (!quantity || quantity < 1) {
    //   e.preventDefault();
    //   // Blur event will show validator message
    //   formApi.blur('quantity');
    //   formApi.focus('quantity');
    // } else if (!deliveryMethod) {
    //   e.preventDefault();
    //   // Blur event will show validator message
    //   formApi.blur('deliveryMethod');
    //   formApi.focus('deliveryMethod');
    // } else {
    handleSubmit(e);
    // }
  };

  const breakdownData = {};
  const showBreakdown =
    breakdownData && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;
  const breakdown = showBreakdown ? (
    <div className={css.breakdownWrapper}>
      <h3>
        <FormattedMessage id="ProductOrderForm.breakdownTitle" />
      </h3>
      <EstimatedCustomerBreakdownMaybe
        unitType={config.lineItemUnitType}
        breakdownData={breakdownData}
        lineItems={lineItems}
      />
    </div>
  ) : null;

  const showContactUser = typeof onContactUser === 'function';

  const onClickContactUser = e => {
    e.preventDefault();
    onContactUser();
  };

  const contactSellerLink = (
    <InlineTextButton onClick={onClickContactUser}>
      <FormattedMessage id="ProductOrderForm.finePrintNoStockLinkText" />
    </InlineTextButton>
  );
  const quantityRequiredMsg = intl.formatMessage({ id: 'ProductOrderForm.quantityRequired' });

  const hasStock = currentStock !== 0;
  const quantities = hasStock ? [...Array(currentStock).keys()].map(i => i + 1) : [];
  const hasNoStockLeft = (typeof currentStock != null && currentStock === 0) || true;
  const hasOneItemLeft = (typeof currentStock != null && currentStock === 1) || true;

  const submitInProgress = fetchLineItemsInProgress;
  const submitDisabled = !hasStock;

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormSpy subscription={{ values: true }} onChange={handleOnChange} />

      {breakdown}
      <div className={css.submitButton}>
        <p className={css.finePrint}></p>
        {!sellerShippingAddress?.length > 0 && !transaction && (
          <p>
            <FormattedMessage id="ListingPage.shippingText" />
          </p>
        )}
        <PrimaryButton
          type="submit"
          inProgress={submitInProgress}
          disabled={
            !isStripeConnected ||
            (!sellerShippingAddress?.length > 0 && !transaction) ||
            currentStock === 0 ||
            isUnavailable ||
            !showEmailVerification
              ? true
              : false
          }
        >
          {isUnavailable ? (
            <FormattedMessage
              id={
                isClosed
                  ? 'ProductOrderForm.noLongerAvailableButton'
                  : 'ProductOrderForm.soldButton'
              }
            />
          ) : hasStock ? (
            <FormattedMessage id="ProductOrderForm.ctaButton" />
          ) : (
            <FormattedMessage id="ProductOrderForm.ctaButtonNoStock" />
          )}
        </PrimaryButton>
      </div>
    </Form>
  );
};

const ProductOrderForm = props => {
  const intl = useIntl();
  const { price, currentStock = 1, pickupEnabled, shippingEnabled } = props;

  // Should not happen for listings that go through EditListingWizard.
  // However, this might happen for imported listings.
  // if (!pickupEnabled && !shippingEnabled) {
  //   return (
  //     <p className={css.error}>
  //       <FormattedMessage id="ProductOrderForm.noDeliveryMethodSet" />
  //     </p>
  //   );
  // }

  // if (!price) {
  //   return (
  //     <p className={css.error}>
  //       <FormattedMessage id="ProductOrderForm.listingPriceMissing" />
  //     </p>
  //   );
  // }
  // if (price.currency !== config.currency) {
  //   return (
  //     <p className={css.error}>
  //       <FormattedMessage id="ProductOrderForm.listingCurrencyInvalid" />
  //     </p>
  //   );
  // }
  const hasOneItemLeft = currentStock && currentStock === 1;
  const quantityMaybe = hasOneItemLeft ? { quantity: '1' } : {};
  const singleDeliveryMethodAvailableMaybe =
    shippingEnabled && !pickupEnabled
      ? { deliveryMethod: 'shipping' }
      : !shippingEnabled && pickupEnabled
      ? { deliveryMethod: 'pickup' }
      : {};
  const hasMultipleDeliveryMethods = pickupEnabled && shippingEnabled;
  const initialValues = { ...quantityMaybe, ...singleDeliveryMethodAvailableMaybe };

  return (
    <FinalForm
      initialValues={initialValues}
      hasMultipleDeliveryMethods={hasMultipleDeliveryMethods}
      {...props}
      intl={intl}
      render={renderForm}
    />
  );
};

ProductOrderForm.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  currentStock: null,
  listingId: null,
  isOwnListing: false,
  isUnavailable: false,
  isClosed: false,
  lineItems: null,
  fetchLineItemsError: null,
};

ProductOrderForm.propTypes = {
  rootClassName: string,
  className: string,

  // form
  formId: string.isRequired,
  onSubmit: func.isRequired,

  // listing
  listingId: propTypes.uuid,
  price: propTypes.money,
  currentStock: number,
  isOwnListing: bool,
  isUnavailable: bool,
  isClosed: bool,

  // line items
  lineItems: propTypes.lineItems,
  onFetchTransactionLineItems: func.isRequired,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // other
  onContactUser: func,
};

export default ProductOrderForm;
